import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import styled from 'styled-components';

import storeLoader from 'app/stores/storeLoader';
import Field from 'core/form/components/Field';
import InputGroup from 'core/form/components/InputGroup';
import InputMultiple from 'core/form/components/InputMultiple';
import Switch from 'core/form/components/Switch';
import RadioGroup from 'core/form/components/RadioGroup';
import Select from 'core/form/components/select/Select';
import TextArea from 'core/form/components/TextArea';
import ToggleButtonGroup from 'core/form/components/ToggleButtonGroup';
import formConsumer from 'core/form/formConsumer';
import Box from 'core/components/Box';
import Flex from 'core/components/Flex';
import Icon from 'core/components/Icon';
import Image from 'core/components/Image';
import Tooltip from 'core/components/Tooltip';
import LinkButton from 'core/components/LinkButton';
import Spinner from 'core/components/Spinner';
import Tag from 'core/components/Tag';
import Text from 'core/components/Text';
import Heading from 'core/components/Heading';

import { fields } from 'app/forms/config/notification';
import { Checkbox } from 'core/form';
import {
  CHANNEL_TYPES,
  JIRA_CLOUD_URL_TYPES,
  JIRA_CLOUD_ISSUE_TYPES,
  JIRA_CLOUD_ISSUE_TYPE_CUSTOM
} from '@kentik/ui-shared/notifications/constants';
import channelTypes from './channelTypes';
import CustomHeadersEditor from './CustomHeadersEditor';

const InlineRadioField = styled.div`
  display: flex;
  align-items: center;

  .bp4-label {
    margin-right: 12px;
    margin-bottom: 8px;
  }

  .bp4-control {
    margin-bottom: 0 !important;
  }
`;

@storeLoader('$notifications.supportedChannels')
@formConsumer
@observer
export default class NotificationSettings extends Component {
  static propTypes = {
    model: PropTypes.object.isRequired,
    filter: PropTypes.array
  };

  static defaultProps = {
    filter: []
  };

  componentDidMount() {
    const { form } = this.props;

    this.handleTypeChange(undefined, this.selectedType);

    this.setState({
      url: form.getValue('config.sendingConfig.url'),
      requestTlsVerify: form.getValue('config.sendingConfig.requestTlsVerify'),
      oauth2TlsVerify: form.getValue('config.sendingConfig.oauth2TlsVerify'),
      oauth2AccessTokenURL: form.getValue('config.sendingConfig.oauth2AccessTokenURL'),
      oauth2ClientId: form.getValue('config.sendingConfig.oauth2ClientId'),
      oauth2ClientSecret: form.getValue('config.sendingConfig.oauth2ClientSecret')
    });
  }

  state = {
    selectedType: undefined,
    sendDaily: undefined
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  get sendDaily() {
    const { sendDaily } = this.state;
    const { form } = this.props;
    const insightsDigestField = form.getField('config.renderingConfig.insightsDigest');

    return sendDaily === undefined ? !!insightsDigestField.value : sendDaily;
  }

  get selectedType() {
    const { model } = this.props;
    const { selectedType } = this.state;

    return selectedType || model.get('channelType');
  }

  get selectedChannel() {
    const { $notifications } = this.props;

    return $notifications.supportedChannels.models.find((model) => model.get('channelType') === this.selectedType);
  }

  @computed
  get channelOptions() {
    const { $notifications, filter } = this.props;
    let channels = $notifications.supportedChannels.get();

    if (filter.length > 0) {
      channels = channels.filter((channel) => filter.includes(channel.get('channelType')));
    }

    if (this.readOnly) {
      channels = channels.filter((channel) => channel.get('channelType') === this.selectedType);
    }

    return channels
      .map((channel) => channel.get())
      .sort(({ channelDisplayName }, { channelDisplayName: otherDisplayName }) =>
        (channelDisplayName || '').localeCompare(otherDisplayName)
      )
      .map((channel) => {
        const { channelType, channelDisplayName } = channel;
        const knownIntegration = channelTypes[channelType];
        const integration = knownIntegration
          ? {
              ...knownIntegration,
              type: channelType,
              name: knownIntegration.name || channelDisplayName
            }
          : { name: channelDisplayName, type: channelType };

        return this.renderIntegration(integration);
      });
  }

  get readOnly() {
    const { model } = this.props;
    const { config = {} } = model.get();
    return !!config.userChannelUserID;
  }

  handleValueChange = (field, value) => {
    const { form } = this.props;
    // we track url changes, to update, if necessary, OAuth 2.0 fields
    // we're essentially making this component re-render, because otherwise it wouldn't
    if (field.name === 'config.sendingConfig.url') {
      this.setState({ url: value });
    } else if (field.name === 'config.sendingConfig.oauth2AccessTokenURL') {
      this.setState({ oauth2AccessTokenURL: value });
    } else if (field.name === 'config.sendingConfig.oauth2ClientId') {
      this.setState({ oauth2ClientId: value });
    } else if (field.name === 'config.sendingConfig.oauth2ClientSecret') {
      this.setState({ oauth2ClientSecret: value });
    } else if (field.name === 'config.sendingConfig.requestTlsVerify') {
      this.setState({ requestTlsVerify: value });

      // clear out corresponding PEM
      if (!value) {
        form.setValue('config.sendingConfig.requestTlsPem', '');
      }
    } else if (field.name === 'config.sendingConfig.oauth2TlsVerify') {
      this.setState({ oauth2TlsVerify: value });

      // clear out corresponding PEM
      if (!value) {
        form.setValue('config.sendingConfig.oauth2TlsPem', '');
      }
    }
  };

  // No binding needed in the constructor!
  handleTypeChange = (field, newValue) => {
    const { form, model } = this.props;
    const usernamesField = form.getField('config.sendingConfig.usernames');
    const customHeadersField = form.getField('config.sendingConfig.customHeaders');
    const urlField = form.getField('config.sendingConfig.url');

    if (
      ['custom-webhook', 'json', 'msteams', 'slack', 'splunk', 'victorops', 'xmatters', 'jira-cloud'].includes(newValue)
    ) {
      urlField.setRules('required|unsanitaryURL');
    } else {
      urlField.setRules('');
    }

    if (newValue === 'email') {
      usernamesField.setRules('required|array|min:1');
      usernamesField.setChildren({
        rules: 'required|email'
      });

      if (usernamesField.fieldStates.length < 1) {
        usernamesField.add(undefined);
      }
    } else {
      usernamesField.setValue([]);
      usernamesField.setRules('');
    }

    if (newValue !== 'custom-webhook' && newValue !== 'jira-cloud') {
      customHeadersField.setValue([]);
    }

    const usernameField = form.getField('config.sendingConfig.username');
    const tokenField = form.getField('config.sendingConfig.token');
    const projectKeyField = form.getField('config.jiraCloudConfig.projectKey');
    const issueTypeField = form.getField('config.jiraCloudConfig.issueType');

    if (newValue === 'jira-cloud') {
      const urlType = form.getField('config.jiraCloudConfig.urlType');
      const isSite = !urlType || urlType.value === JIRA_CLOUD_URL_TYPES.SITE;

      usernameField.setRules(isSite ? 'required' : '');
      tokenField.setRules(isSite ? 'required' : '');
      projectKeyField.setRules('required');
      issueTypeField.setRules('required');

      if (!issueTypeField.value) {
        issueTypeField.setValue(JIRA_CLOUD_ISSUE_TYPES.TASK);
      }
    } else {
      usernameField.setRules('');
      tokenField.setRules('');
      projectKeyField.setRules('');
      issueTypeField.setRules('');
    }

    if (model) {
      model.set('channelType', newValue);
    }

    this.setState({
      selectedType: newValue
    });
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  handleSendDailyChange = (field, newValue) => {
    this.setState({
      sendDaily: newValue
    });
  };

  renderIntegration = ({ name, type, logo, icon, image, text, height, showName, beta }) => {
    const imgHeight = height || 32;
    const logoProps = {
      height: imgHeight,
      width: '100%',
      style: { width: 'auto' }
    };

    return {
      value: type,
      label: (
        <Flex
          key={type}
          width={84}
          height={64}
          m={1}
          flexDirection={showName ? 'column' : 'row'}
          alignItems="center"
          justifyContent="center"
          position="relative"
          color="muted"
          gap={showName ? '4px' : undefined}
        >
          {icon && <Icon icon={icon} {...logoProps} color="muted" iconSize={imgHeight} />}
          {logo}
          {image && <Image src={image} {...logoProps} />}
          {text && (
            <Text as="div" mb="4px" fontSize={24} fontWeight="bold" muted>
              {text}
            </Text>
          )}
          {!logo && !icon && !image && !text && (
            <Text large fontWeight="bold">
              {name}
            </Text>
          )}

          {showName && (
            <Text
              small
              fontWeight="bold"
              color="body"
              mt="4px"
              style={{
                textAlign: 'center',
                lineHeight: '1.2',
                width: '100%',
                whiteSpace: 'normal',
                wordWrap: 'break-word'
              }}
            >
              {name}
            </Text>
          )}

          {beta && (
            <Tag small minimal intent="warning" style={{ position: 'absolute', top: '-7px', right: '-7px' }}>
              <Text small fontSize="10px" fontWeight="bold">
                Beta
              </Text>
            </Tag>
          )}
        </Flex>
      )
    };
  };

  get renderOAuthFields() {
    const { $notifications } = this.props;
    const { oauth2AccessTokenURL, oauth2TlsVerify, oauth2ClientId, oauth2ClientSecret } = this.state;

    if ($notifications.channelTypeSupportOAuth(this.selectedType)) {
      const showOauth2Tls = (oauth2AccessTokenURL || '').startsWith('https://');
      const showOauth2TlsPem = showOauth2Tls && oauth2TlsVerify;

      // Check if any of the three OAuth credential fields have values
      const hasAnyOAuthValue = Boolean(oauth2AccessTokenURL || oauth2ClientId || oauth2ClientSecret);

      return (
        <Box>
          <Flex gap={1} mt={2} mb={1} justifyContent="space-between">
            <Heading level={4} mb={0}>
              OAuth 2.0
            </Heading>
          </Flex>

          <Field
            name="config.sendingConfig.oauth2AccessTokenURL"
            labelInfo=""
            showRequired={hasAnyOAuthValue}
            onChange={this.handleValueChange}
          >
            <InputGroup />
          </Field>
          {showOauth2Tls && (
            <Field
              name="config.sendingConfig.oauth2TlsVerify"
              labelInfo=""
              showLabel={false}
              onChange={this.handleValueChange}
            >
              <Checkbox />
            </Field>
          )}
          {showOauth2TlsPem && (
            <Field name="config.sendingConfig.oauth2TlsPem" labelInfo="">
              <TextArea width="100%" autoGrow style={{ maxHeight: '300px' }} />
            </Field>
          )}
          {showOauth2Tls && (
            <Flex>
              <Field name="config.sendingConfig.oauth2TlsClientCertPem" labelInfo="" flex={1} mr={2}>
                <TextArea width="100%" autoGrow style={{ maxHeight: '300px' }} />
              </Field>
              <Field name="config.sendingConfig.oauth2TlsClientKeyPem" labelInfo="" flex={1} ml={2}>
                <TextArea width="100%" autoGrow style={{ maxHeight: '300px' }} />
              </Field>
            </Flex>
          )}

          <Field
            name="config.sendingConfig.oauth2ClientId"
            labelInfo=""
            showRequired={hasAnyOAuthValue}
            onChange={this.handleValueChange}
          >
            <InputGroup />
          </Field>
          <Field
            name="config.sendingConfig.oauth2ClientSecret"
            labelInfo=""
            showRequired={hasAnyOAuthValue}
            onChange={this.handleValueChange}
          >
            <InputGroup />
          </Field>
          <Field name="config.sendingConfig.oauth2Scopes">
            <InputGroup />
          </Field>
        </Box>
      );
    }

    return null;
  }

  get renderBaseFields() {
    const requiredFields = this.selectedChannel.get('requiredFields') || [];
    const urlField = requiredFields.find((field) => field.path === 'sendingConfig.url');
    const isCustomWebhook = this.selectedType === 'custom-webhook';
    const isEmail = this.selectedType === 'email';

    if (requiredFields.length > 0 && urlField && isCustomWebhook) {
      const { url, requestTlsVerify } = this.state;
      const name = 'config.sendingConfig.url';
      const formConfig = fields[name];
      const helpText = formConfig?.helpText || urlField.description;
      const showRequestTls = (url || '').startsWith('https://');
      const showRequestTlsPem = showRequestTls && requestTlsVerify;

      return (
        <>
          <Field
            key={`${urlField.path}-${helpText}`}
            flex={1}
            name={name}
            helpText={helpText}
            disabled={this.readOnly}
            onChange={this.handleValueChange}
          >
            <InputGroup type={formConfig.type || 'text'} />
          </Field>
          {showRequestTls && (
            <Field
              name="config.sendingConfig.requestTlsVerify"
              labelInfo=""
              showLabel={false}
              onChange={this.handleValueChange}
            >
              <Checkbox />
            </Field>
          )}
          {showRequestTlsPem && (
            <Field name="config.sendingConfig.requestTlsPem" labelInfo="">
              <TextArea width="100%" autoGrow style={{ maxHeight: '300px' }} />
            </Field>
          )}
          {showRequestTls && (
            <>
              <Field name="config.sendingConfig.requestTlsClientCertPem" labelInfo="" flex={1}>
                <TextArea width="100%" autoGrow style={{ maxHeight: '300px' }} marginRight={1} />
              </Field>
              <Field name="config.sendingConfig.requestTlsClientKeyPem" labelInfo="" flex={1}>
                <TextArea width="100%" autoGrow style={{ maxHeight: '300px' }} />
              </Field>
            </>
          )}
          {this.renderCustomHeaders}
        </>
      );
    }

    if (isEmail) {
      return (
        <>
          <Field
            key="config.renderingConfig.emailConfig.useSubjectCustomTemplate"
            flex={1}
            mr="20px"
            showLabel={false}
            name="config.renderingConfig.emailConfig.useSubjectCustomTemplate"
            onChange={this.handleValueChange}
          >
            <Checkbox />
          </Field>
          <Field
            key="config.renderingConfig.emailConfig.useBodyCustomTemplate"
            flex={1}
            mr="20px"
            showLabel={false}
            name="config.renderingConfig.emailConfig.useBodyCustomTemplate"
            onChange={this.handleValueChange}
          >
            <Checkbox />
          </Field>
          <Field
            key="config.renderingConfig.emailConfig.sendAsPlainText"
            flex={1}
            showLabel={false}
            name="config.renderingConfig.emailConfig.sendAsPlainText"
            onChange={this.handleValueChange}
          >
            <Checkbox />
          </Field>
        </>
      );
    }

    if (this.selectedType === CHANNEL_TYPES.SERVICENOW) {
      return (
        <InlineRadioField>
          <Field name="config.serviceNowConfig.apiType" showLabel={false} m={0} p={0} pb={2} disabled={this.readOnly}>
            <RadioGroup
              inline
              label={
                <>
                  <Tooltip
                    content={<Box maxWidth={350}>{fields['config.serviceNowConfig.apiType'].tooltip}</Box>}
                    boundary="window"
                    minimal
                    hoverOpenDelay={150}
                    hoverCloseDelay={100}
                  >
                    <Icon iconSize={16} icon="info-sign" color="gray3" mr="6px" style={{ cursor: 'pointer' }} />
                  </Tooltip>
                  API Type:
                </>
              }
            />
          </Field>
        </InlineRadioField>
      );
    }

    if (this.selectedType === CHANNEL_TYPES.JIRA_CLOUD) {
      return this.renderJiraCloudFields;
    }

    return null;
  }

  get renderCustomHeaders() {
    return (
      <Field
        name="config.sendingConfig.customHeaders"
        disabled={this.readOnly}
        labelInfo={
          <Tooltip
            content={<Box maxWidth={350}>Content-Type will be set to application/json unless specified</Box>}
            boundary="window"
            minimal
            hoverOpenDelay={150}
            hoverCloseDelay={100}
          >
            <Icon iconSize={14} icon="info-sign" color="gray3" ml="4px" style={{ cursor: 'pointer' }} />
          </Tooltip>
        }
      >
        <CustomHeadersEditor />
      </Field>
    );
  }

  handleJiraUrlTypeChange = (field, newValue) => {
    const { form } = this.props;
    const urlField = form.getField('config.sendingConfig.url');
    const usernameField = form.getField('config.sendingConfig.username');
    const tokenField = form.getField('config.sendingConfig.token');

    if (newValue === JIRA_CLOUD_URL_TYPES.SITE) {
      usernameField.setRules('required');
      tokenField.setRules('required');
    } else {
      usernameField.setRules('');
      tokenField.setRules('');
    }

    urlField.setRules('required|unsanitaryURL');
    this.handleValueChange(field, newValue);
  };

  handleJiraIssueTypeChange = (field, value) => {
    const { form } = this.props;
    const issueTypeField = form.getField('config.jiraCloudConfig.issueType');

    if (value === JIRA_CLOUD_ISSUE_TYPE_CUSTOM) {
      issueTypeField.setValue('');
    } else {
      issueTypeField.setValue(value);
    }
  };

  get jiraCloudUrlType() {
    const { form } = this.props;
    const field = form.getField('config.jiraCloudConfig.urlType');
    return field ? field.value : JIRA_CLOUD_URL_TYPES.SITE;
  }

  get jiraCloudIsSiteMode() {
    return this.jiraCloudUrlType === JIRA_CLOUD_URL_TYPES.SITE;
  }

  get jiraCloudIsCustomIssueType() {
    const { form } = this.props;
    const field = form.getField('uiProperties.jiraCloudIssueTypeMode');
    return field ? field.value === JIRA_CLOUD_ISSUE_TYPE_CUSTOM : false;
  }

  get renderJiraCloudFields() {
    const isCustomIssueType = this.jiraCloudIsCustomIssueType;

    return (
      <>
        <InlineRadioField>
          <Field
            name="config.jiraCloudConfig.urlType"
            showLabel={false}
            m={0}
            p={0}
            pb={2}
            disabled={this.readOnly}
            onChange={this.handleJiraUrlTypeChange}
          >
            <RadioGroup
              inline
              label={
                <>
                  <Tooltip
                    content={<Box maxWidth={350}>{fields['config.jiraCloudConfig.urlType'].tooltip}</Box>}
                    boundary="window"
                    minimal
                    hoverOpenDelay={150}
                    hoverCloseDelay={100}
                  >
                    <Icon iconSize={16} icon="info-sign" color="gray3" mr="6px" style={{ cursor: 'pointer' }} />
                  </Tooltip>
                  Connection Type:
                </>
              }
            />
          </Field>
        </InlineRadioField>

        <Field name="config.sendingConfig.url" disabled={this.readOnly}>
          <InputGroup />
        </Field>

        {this.jiraCloudIsSiteMode && (
          <>
            <Field name="config.sendingConfig.username" disabled={this.readOnly}>
              <InputGroup />
            </Field>
            <Field name="config.sendingConfig.token" disabled={this.readOnly}>
              <InputGroup />
            </Field>
          </>
        )}

        {!this.jiraCloudIsSiteMode && this.renderCustomHeaders}

        <Field name="config.jiraCloudConfig.projectKey" disabled={this.readOnly}>
          <InputGroup />
        </Field>

        <Text fontWeight="bold" mb="4px">
          Issue Type
        </Text>
        <Flex alignItems="flex-start" mb={1}>
          <Field
            name="uiProperties.jiraCloudIssueTypeMode"
            disabled={this.readOnly}
            onChange={this.handleJiraIssueTypeChange}
            showLabel={false}
            mb={0}
            mr={2}
          >
            <Select />
          </Field>
          {isCustomIssueType && (
            <Field name="config.jiraCloudConfig.issueType" disabled={this.readOnly} showLabel={false} mb={0} flex={1}>
              <InputGroup />
            </Field>
          )}
        </Flex>
      </>
    );
  }

  get renderRequiredFields() {
    const { handleTabChange } = this.props;
    const requiredFields = this.selectedChannel.get('requiredFields') || [];

    if (this.selectedType === CHANNEL_TYPES.JIRA_CLOUD) {
      return null;
    }

    return requiredFields
      .filter(
        (field) =>
          // we don't want to show the URL field here for custom webhook, we handle that separately and there's additional logic related to it
          (field.path !== 'sendingConfig.url' || this.selectedType !== 'custom-webhook') &&
          // custom headers are rendered via CustomHeadersEditor in renderBaseFields
          field.path !== 'sendingConfig.customHeaders'
      )
      .map((field, index) => {
        const name = `config.${field.path}`;
        const formConfig = fields[name] || fields[`${name}[]`];
        const useSlider = formConfig && (formConfig.defaultValue === true || formConfig.defaultValue === false);
        const useSelect = formConfig?.options?.length > 0;
        let helpText = formConfig?.helpText || field.description;
        let FieldComponent = <InputGroup type={formConfig && formConfig.type ? formConfig.type : 'text'} />;

        if (!formConfig) {
          return null;
        }

        if (useSlider || helpText === formConfig.label) {
          helpText = '';
        }

        if (useSlider) {
          FieldComponent = <Switch pt={1} />;
        }

        if (useSelect) {
          FieldComponent = <Select pt={1} />;
        }

        if (name === 'config.renderingConfig.customTemplate') {
          FieldComponent = <div />;
          helpText = (
            <>
              <LinkButton onClick={() => handleTabChange('notification-templates')}>
                Go to Notification Template Setup
              </LinkButton>
            </>
          );
        }

        if (formConfig.children) {
          FieldComponent = (
            <InputMultiple containerProps={{ width: '100%' }} addButtonText="Add" disabled={this.readOnly} />
          );
        }

        return (
          <Field
            autoFocus={index === 0}
            key={`${field.path}-${helpText}`}
            flex={1}
            name={name}
            helpText={helpText}
            disabled={this.readOnly}
            onChange={this.handleValueChange}
          >
            {FieldComponent}
          </Field>
        );
      });
  }

  render() {
    const { loading, role } = this.props;
    if (loading) {
      return <Spinner />;
    }
    const ariaProps = {};
    if (role) {
      ariaProps.role = role;
    }

    return (
      <Box {...ariaProps}>
        <Flex mb={1}>
          <Field name="enabled" width={115} mr={2} onChange={this.handleEnabledChange}>
            <Switch switchLabel="Status" pt={1} />
          </Field>

          <Box flex={1}>
            <Field name="name" disabled={this.readOnly}>
              <InputGroup />
            </Field>
          </Box>
        </Flex>

        <Flex flex={1} mt={1} mb={2}>
          <Field
            name="channelType"
            onChange={this.handleTypeChange}
            options={this.channelOptions}
            disabled={this.readOnly}
          >
            <ToggleButtonGroup
              containerProps={{ groupProps: { wrap: 'wrap' } }}
              buttonProps={{ mt: '4px', ml: '4px' }}
            />
          </Field>
        </Flex>

        {this.renderBaseFields}
        {this.renderRequiredFields}
        {this.renderOAuthFields}

        {/* this is currently hidden - need to figure out what the exact approach should be - v4 needs parity work */}
        {/* <Field name="reminder_interval"> */}
        {/*  <Select /> */}
        {/* </Field> */}
      </Box>
    );
  }
}
